import { GuideButton } from '@/cowork/guide/GuidePanel';
import { useRef } from 'react';
import { Sparkles, Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HeaderSearchBar } from '@/components/HeaderSearchBar';
import { UserProfileMenu } from '@/components/UserProfileMenu';
import { BrandBadge } from '@/cowork/BrandBadge';
import beatlyLogo from 'figma:asset/78166de18001e30ac00ef2150ee31fcaaf218dc5.png';

interface TopNavBarProps {
  currentPage?: string;
  onPageChange?: (page: string) => void;
  sidebarCollapsed?: boolean;
  userName?: string;
  brandName?: string;
  activeMode?: 'chat' | 'cowork' | 'platform';
  onModeChange?: (mode: 'chat' | 'cowork' | 'platform') => void;
  /** When false, hide Platform/Cowork/Chat mode switch (staging-only feature). */
  showCowork?: boolean;
  /** Header search catalog + recents — same as {@link AppSidebar}. */
  lastCampaignPath?: string | null;
  lastCampaignDisplayName?: string | null;
}

/** Temporarily hide AI Assistant + Notifications; set to false to show again. */
const HIDE_TOP_NAV_AI_AND_NOTIFICATIONS = true;

/**
 * App chrome header: logo, search, optional brand badge, and profile menu.
 * Mode pills and the legacy-app CTA are gone — platform/cowork/chat stay URL-only.
 * `activeMode` / `onModeChange` remain on the props type for existing callers.
 */
export function TopNavBar({
  currentPage: _currentPage = '',
  onPageChange,
  activeMode: _activeMode = 'platform',
  onModeChange: _onModeChange,
  showCowork = false,
  lastCampaignPath = null,
  lastCampaignDisplayName = null,
}: TopNavBarProps) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={navRef}
      className="h-14 bg-white flex items-center pr-5 shrink-0 z-50 relative"
    >
      {/* Left: Logo – generous padding, natural sizing */}
      <div className="flex items-center shrink-0 pl-5 pr-4">
        <img src={beatlyLogo} alt={t('nav.logoAlt')} className="h-8 w-auto" />
      </div>

      {/* Spacer keeps logo left and search/profile right; modes stay URL-only. */}
      <div className="flex-1 min-w-0 px-2" />

      {/* Right: Search | AI | Notifications | Profile | Settings */}
      <div className="flex items-center gap-1.5 shrink-0">
        <GuideButton />

        <HeaderSearchBar
          showCowork={showCowork}
          lastCampaignPath={lastCampaignPath}
          lastCampaignDisplayName={lastCampaignDisplayName}
        />

        {!HIDE_TOP_NAV_AI_AND_NOTIFICATIONS ? (
          <>
            {/* AI */}
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-[#be43ff] hover:bg-[#be43ff]/8 transition-colors"
              title={t('nav.aiAssistant')}
              aria-label={t('nav.aiAssistant')}
            >
              <Sparkles className="w-[18px] h-[18px]" />
            </button>

            {/* Notifications */}
            <button
              type="button"
              className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title={t('nav.notifications')}
              aria-label={t('nav.notifications')}
            >
              <Bell className="w-[18px] h-[18px]" />
              <span className="absolute top-1 right-1.5 w-2 h-2 bg-[#a9f600] rounded-full border-2 border-white" />
            </button>
          </>
        ) : null}

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 mx-1.5" />

        {/* Which brand the agents are acting for — beside the profile menu, not
            in place of it: that avatar is the only way to settings and logout. */}
        {showCowork && <BrandBadge />}

        <UserProfileMenu onPageChange={onPageChange} />
      </div>
    </div>
  );
}
